import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, AlertTriangle, Clock, CheckCircle2, XCircle, Bike, Bell, Settings } from "lucide-react";
import { KpiCard } from "@/components/common";
import { api, inr, apiError } from "@/lib/api";

const STAGES = [
  { key: "new", label: "New" }, { key: "confirmed", label: "Confirmed" }, { key: "preparing", label: "Preparing" },
  { key: "ready", label: "Ready" }, { key: "picked_up", label: "Picked Up" }, { key: "out_for_delivery", label: "Out for Delivery" },
];
const SEV = { critical: "bg-red-50 text-bad border-red-200", warning: "bg-amber-50 text-amber-700 border-amber-200", info: "bg-blue-50 text-blue-700 border-blue-200" };

export default function AdminLiveOps() {
  const [ops, setOps] = useState(null);
  const [alerts, setAlerts] = useState({ alerts: [], open_count: 0 });
  const [showSla, setShowSla] = useState(false);
  const [sla, setSla] = useState({ prep: 25, pickup: 15, delivery: 40 });

  const load = () => {
    api.get("/admin/live-ops").then((r) => { setOps(r.data); setSla(r.data.sla); }).catch(() => {});
    api.get("/admin/ops/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, []);

  const resolve = async (a) => { try { await api.post(`/admin/ops/alerts/${a.id}/resolve`, { note: "Resolved from ops center" }); toast.success("Alert resolved"); load(); } catch (e) { toast.error(apiError(e)); } };
  const saveSla = async () => { try { await api.put("/admin/ops/sla-settings", { prep: Number(sla.prep), pickup: Number(sla.pickup), delivery: Number(sla.delivery) }); toast.success("SLA thresholds saved"); setShowSla(false); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!ops) return <div className="p-8 text-center text-ink-600">Loading live operations…</div>;
  const k = ops.kpis;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-xl text-ink flex items-center gap-2"><Activity size={22} className="text-coral" /> Live Operations</h2>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs text-veg font-semibold"><span className="w-2 h-2 rounded-full bg-veg animate-pulse" /> Live · 6s</span>
          <button onClick={() => setShowSla(!showSla)} className="text-xs font-semibold text-ink-600 flex items-center gap-1 bg-white border border-border rounded-lg px-2.5 py-1.5" data-testid="sla-settings-btn"><Settings size={14} /> SLA</button>
        </div>
      </div>

      {showSla && (
        <div className="bg-white rounded-2xl border border-border p-4 flex flex-wrap items-end gap-3" data-testid="sla-settings">
          {["prep", "pickup", "delivery"].map((k2) => (
            <div key={k2}><label className="text-[11px] font-semibold text-ink-600 capitalize">{k2} SLA (min)</label><input type="number" value={sla[k2]} onChange={(e) => setSla((s) => ({ ...s, [k2]: e.target.value }))} className="block w-24 bg-surface rounded-lg px-3 py-2 text-sm outline-none border border-border mt-0.5" data-testid={`sla-${k2}`} /></div>
          ))}
          <button onClick={saveSla} className="bg-coral text-white font-bold px-4 py-2 rounded-lg text-sm" data-testid="save-sla">Save thresholds</button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <KpiCard icon={Activity} label="Active Orders" value={k.active} tone="coral" />
        <KpiCard icon={Clock} label="Delayed" value={k.delayed} tone="gold" />
        <KpiCard icon={AlertTriangle} label="SLA Breaches" value={k.sla_breaches} tone="ink" />
        <KpiCard icon={Bike} label="Out for Delivery" value={k.out_for_delivery} tone="ink" />
        <KpiCard icon={CheckCircle2} label="Delivered Today" value={k.delivered_today} tone="veg" />
        <KpiCard icon={XCircle} label="Cancelled Today" value={k.cancelled_today} tone="ink" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <h3 className="font-bold text-ink mb-2">Order pipeline</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {STAGES.map((s) => (
              <div key={s.key} className="bg-white rounded-2xl border border-border p-2.5" data-testid={`ops-stage-${s.key}`}>
                <div className="flex items-center justify-between mb-1.5"><span className="font-semibold text-sm text-ink">{s.label}</span><span className="text-xs font-bold bg-surface rounded-full px-2 py-0.5">{ops.counts[s.key] || 0}</span></div>
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {(ops.pipeline[s.key] || []).map((o) => (
                    <div key={o.id} className={`rounded-lg p-2 text-xs ${o.delayed ? "bg-red-50 border border-red-200" : "bg-surface"}`}>
                      <div className="flex justify-between font-semibold"><span>#{(o.code || "").slice(-4)}</span><span className={o.delayed ? "text-bad" : "text-ink-600"}>{o.age_min}m</span></div>
                      <div className="text-ink-600 truncate">{o.restaurant_name}</div>
                      <div className="flex justify-between text-ink-600"><span>{inr(o.total)}</span>{o.delivery_partner && <span className="truncate max-w-[80px]">{o.delivery_partner}</span>}</div>
                      {o.delayed && <div className="text-[10px] text-bad font-bold mt-0.5">Delayed · {o.delay_stage}</div>}
                    </div>
                  ))}
                  {(ops.counts[s.key] || 0) === 0 && <p className="text-[11px] text-ink-600 text-center py-2">Empty</p>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-bold text-ink mb-2 flex items-center gap-1.5"><Bell size={16} /> Alert Center <span className="text-xs bg-bad text-white rounded-full px-2">{alerts.open_count}</span></h3>
          <div className="space-y-2 max-h-[520px] overflow-y-auto" data-testid="alert-center">
            {alerts.alerts.length === 0 && <div className="bg-white rounded-2xl border border-border p-6 text-center text-sm text-ink-600">No operational alerts. All systems nominal.</div>}
            {alerts.alerts.map((a) => (
              <div key={a.id} className={`rounded-xl border p-2.5 ${a.status === "resolved" ? "bg-surface border-border opacity-60" : SEV[a.severity]}`} data-testid={`alert-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div><div className="text-xs font-bold uppercase">{a.severity}</div><div className="text-sm">{a.message}</div><div className="text-[10px] text-ink-600 mt-0.5">{a.created_at?.slice(0, 16).replace("T", " ")} · {a.stage}</div></div>
                  {a.status === "open" ? <button onClick={() => resolve(a)} className="text-[11px] font-bold bg-white rounded-lg px-2 py-1 shrink-0" data-testid={`resolve-${a.id}`}>Resolve</button> : <span className="text-[10px] text-veg font-bold shrink-0">Resolved</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {ops.delayed.length > 0 && (
        <div className="bg-white rounded-2xl border border-border p-4">
          <h3 className="font-bold text-ink mb-2 flex items-center gap-1.5"><AlertTriangle size={16} className="text-bad" /> Delayed orders ({ops.delayed.length})</h3>
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[560px]"><thead><tr className="text-left text-xs text-ink-600 uppercase border-b border-border">{["Order", "Restaurant", "Stage", "Age", "Amount"].map((c) => <th key={c} className="py-2 px-2">{c}</th>)}</tr></thead>
            <tbody>{ops.delayed.map((o) => <tr key={o.id} className="border-b border-border last:border-0"><td className="py-2 px-2 font-semibold">#{(o.code || "").slice(-4)}</td><td className="px-2">{o.restaurant_name}</td><td className="px-2"><span className="text-bad font-semibold capitalize">{o.delay_stage}</span></td><td className="px-2 text-bad font-bold">{o.age_min}m</td><td className="px-2">{inr(o.total)}</td></tr>)}</tbody></table></div>
        </div>
      )}
    </div>
  );
}
